import type { Landmark } from '../../../data/types';
import type { Ring } from '../../../geo/types';
import { vesselOutline } from './churchVessel';
import { conventEnclosureRings } from './conventEnclosure';
import { conventRangeRect } from './conventRange';
import { corners } from './genericBlock';
import magdalena from './magdalenaPlan.json';
import barbara from './barbaraPlan.json';
import { toLayout, type PlanFrame } from './planFrame';

export const hasMagdalenaBarbaraPlan = (landmark: Landmark) =>
  ['landmark-sint-maria-magdalena', 'landmark-sint-barbara'].includes(landmark.id);

export function magdalenaBarbaraReading(landmark: Landmark) {
  if (landmark.id === 'landmark-sint-maria-magdalena') return magdalena;
  if (landmark.id === 'landmark-sint-barbara') return barbara;
  throw new Error(`No Magdalena/Barbara component plan for ${landmark.id}`);
}

export function magdalenaBarbaraFrame(landmark: Landmark): PlanFrame {
  const x = landmark.params['anchorX'],
    y = landmark.params['anchorY'];
  if (typeof x !== 'number' || typeof y !== 'number')
    throw new Error(`${landmark.id} lacks its component anchor`);
  return { origin: [x, y], heading: landmark.heading, ring: [] };
}

export const magdalenaBarbaraChapel = (landmark: Landmark) => ({
  ...magdalenaBarbaraReading(landmark).chapel,
  apse: 'flat' as const,
});

export function magdalenaBarbaraRangeFrame(
  plan: PlanFrame,
  part: { readonly u: number; readonly v: number; readonly angleDeg: number }
): PlanFrame {
  return {
    origin: toLayout(plan, [part.u, part.v]),
    heading: plan.heading + (part.angleDeg * Math.PI) / 180,
    ring: [],
  };
}

/** Grounded bodies only: neither court nor private approach becomes a precinct slab. */
export function magdalenaBarbaraFoundations(landmark: Landmark): Ring[] {
  const plan = magdalenaBarbaraFrame(landmark);
  return [
    vesselOutline(magdalenaBarbaraChapel(landmark)).map((p) => toLayout(plan, p)),
    ...magdalenaBarbaraReading(landmark).ranges.flatMap((part) => {
      const rect = conventRangeRect(part),
        frame = magdalenaBarbaraRangeFrame(plan, part),
        centre = 'passageU' in part ? part.passageU : 0;
      return (
        part.passage
          ? [
              { ...rect, u1: centre - part.passage / 2 },
              { ...rect, u0: centre + part.passage / 2 },
            ]
          : [rect]
      ).map((r) => corners(r).map((p) => toLayout(frame, p)));
    }),
  ];
}

export function magdalenaBarbaraObstacles(landmark: Landmark): Ring[] {
  const plan = magdalenaBarbaraFrame(landmark);
  return [
    ...magdalenaBarbaraFoundations(landmark),
    ...conventEnclosureRings(magdalenaBarbaraReading(landmark).walls).map((r) =>
      r.map((p) => toLayout(plan, p))
    ),
  ];
}

export function magdalenaBarbaraFloors(landmark: Landmark) {
  const plan = magdalenaBarbaraFrame(landmark),
    reading = magdalenaBarbaraReading(landmark);
  return [
    ...reading.floors.map((f) => ({ ...f, ring: f.ring.map((p) => [p[0]!, p[1]!] as const) })),
    ...reading.ranges
      .filter((p) => p.passage)
      .map((p) => {
        const centre = 'passageU' in p ? p.passageU : 0;
        return {
          ring: corners({
            u0: centre - p.passage / 2,
            u1: centre + p.passage / 2,
            v0: -p.width / 2,
            v1: p.width / 2,
          }).map((q) =>
            toLayout({ origin: [p.u, p.v], heading: (p.angleDeg * Math.PI) / 180, ring: [] }, q)
          ),
          height: 0.025,
          garden: false,
        };
      }),
  ].map((f) => ({ ...f, ring: f.ring.map((p) => toLayout(plan, p)) }));
}
